import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ChevronLeftOutlinedIcon from "@mui/icons-material/ChevronLeftOutlined";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import { feriaPhotoDisplayUrl } from "../../api/Ferias/FeriasApi";

const isVideoPhoto = (photo) => {
  const mime = String(photo?.mimeType || "").toLowerCase();
  return (
    photo?.kind === "video" ||
    mime.startsWith("video/") ||
    /\/videos\//i.test(String(photo?.fileUrl || ""))
  );
};

/**
 * Carrusel de fotos/vídeos de sesión de feria (estilo Meetings).
 */
export default function FeriaMediaPreviewDialog({
  open,
  photo,
  onClose,
  onPrev = null,
  onNext = null,
  canPrev = false,
  canNext = false,
  navIndex = -1,
  navTotal = 0,
}) {
  const theme = useTheme();
  const [mediaReady, setMediaReady] = useState(false);
  const video = isVideoPhoto(photo);
  const src = feriaPhotoDisplayUrl(photo);
  const mediaStageH = { xs: "min(52vh, 420px)", md: "min(70vh, 720px)" };

  useEffect(() => {
    setMediaReady(false);
  }, [photo?.id, src]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowLeft" && canPrev) {
        e.preventDefault();
        onPrev?.();
      } else if (e.key === "ArrowRight" && canNext) {
        e.preventDefault();
        onNext?.();
      } else if (e.key === "Escape") {
        onClose?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, canPrev, canNext, onPrev, onNext, onClose]);

  return (
    <Dialog
      open={open && Boolean(photo)}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.55)",
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          bgcolor: "background.paper",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          pr: 1,
          py: 1.5,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            {video ? "Vídeo" : "Foto"}
            {photo?.id ? ` #${photo.id}` : ""}
          </Typography>
          {navTotal > 1 && navIndex >= 0 ? (
            <Chip
              size="small"
              variant="outlined"
              label={`${navIndex + 1} / ${navTotal}`}
              sx={{ height: 24, fontWeight: 800, borderRadius: 1.25 }}
            />
          ) : null}
        </Stack>
        <IconButton aria-label="Cerrar" onClick={onClose} size="small">
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, pb: 2.5 }}>
        {photo ? (
          <Box
            sx={{
              position: "relative",
              height: mediaStageH,
              minHeight: mediaStageH,
              borderRadius: 2,
              bgcolor: video
                ? "common.black"
                : alpha(theme.palette.text.primary, 0.04),
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {!mediaReady ? (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "grid",
                  placeItems: "center",
                  zIndex: 1,
                  pointerEvents: "none",
                }}
              >
                <CircularProgress size={28} thickness={4} />
              </Box>
            ) : null}

            {src ? (
              video ? (
                <Box
                  component="video"
                  key={`feria-prev-v-${photo.id}`}
                  src={src}
                  controls
                  autoPlay
                  playsInline
                  onLoadedData={() => setMediaReady(true)}
                  onError={() => setMediaReady(true)}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    display: "block",
                    opacity: mediaReady ? 1 : 0.35,
                  }}
                />
              ) : (
                <Box
                  component="img"
                  key={`feria-prev-i-${photo.id}`}
                  src={src}
                  alt=""
                  onLoad={() => setMediaReady(true)}
                  onError={() => setMediaReady(true)}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    display: "block",
                    opacity: mediaReady ? 1 : 0.35,
                  }}
                />
              )
            ) : (
              <Stack
                alignItems="center"
                spacing={1}
                sx={{ color: "text.secondary" }}
              >
                <VideocamOutlinedIcon />
                <Typography variant="body2">Sin vista previa</Typography>
              </Stack>
            )}

            {canPrev || canNext ? (
              <>
                <IconButton
                  aria-label="Anterior"
                  onClick={() => onPrev?.()}
                  disabled={!canPrev}
                  sx={{
                    position: "absolute",
                    left: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    bgcolor: alpha(theme.palette.common.black, 0.45),
                    color: "common.white",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.common.black, 0.65),
                    },
                    "&.Mui-disabled": {
                      bgcolor: alpha(theme.palette.common.black, 0.2),
                      color: alpha(theme.palette.common.white, 0.35),
                    },
                  }}
                >
                  <ChevronLeftOutlinedIcon />
                </IconButton>
                <IconButton
                  aria-label="Siguiente"
                  onClick={() => onNext?.()}
                  disabled={!canNext}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    bgcolor: alpha(theme.palette.common.black, 0.45),
                    color: "common.white",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.common.black, 0.65),
                    },
                    "&.Mui-disabled": {
                      bgcolor: alpha(theme.palette.common.black, 0.2),
                      color: alpha(theme.palette.common.white, 0.35),
                    },
                  }}
                >
                  <ChevronRightOutlinedIcon />
                </IconButton>
              </>
            ) : null}
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
